<template>
  <generic-modal
    :title="$t('staff member')"
    :buttons="buttons"
    ref="generic_modal"
  >
    <!-- Navigation Tabs -->
    <div class="tabs is-toggle is-fullwidth" ref="tabs">
      <ul>
        <li
          class="is-active"
          data-tab="general"
          @click="change_active_tab($event, this)"
        >
          <a>
            <span class="icon">
              <i class="info icon"></i>
            </span>
            <span>{{ $t("general") }}</span>
          </a>
        </li>
        <li data-tab="images" @click="change_active_tab($event, this)">
          <a>
            <span class="icon">
              <i class="camera icon"></i>
            </span>
            <span>{{ $t("photos") }}</span>
          </a>
        </li>
        <li data-tab="custom_fields" @click="change_active_tab($event, this)">
          <a>
            <span class="icon">
              <i class="cubes icon"></i>
            </span>
            <span>{{ $t("custom fields") }}</span>
          </a>
        </li>
      </ul>
    </div>

    <!-- Tab Content Container -->
    <div class="is-fluid" ref="contents">
      <!-- General Information Tab -->
      <div class="tab-content" data-tab="general">
        <form>
          <!-- System Information Section -->
          <div class="box mb-5">
            <h6 class="title is-6 mb-4">{{ $t("system information") }}</h6>
            <div class="columns">
              <div class="column is-4">
                <div class="field">
                  <label class="label">{{ $t("internal id") }}</label>
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      v-model="meta.internal_id_number"
                      :placeholder="$t('enter internal id')"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="columns">
              <div class="column">
                <div class="field">
                  <generic-dropdown
                    :title="$t('modul user')"
                    :placeholder="$t('select one')"
                    :is_multiple="false"
                    url="/access/user/catalog"
                    name_field="username"
                    value_field="user_id"
                    @selected="set_modul_user"
                    ref="modul_user_dropdown"
                  />
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <generic-dropdown
                    :title="$t('status')"
                    :placeholder="$t('select one')"
                    :is_multiple="false"
                    url="/staff/status/catalog"
                    name_field="name"
                    value_field="status_id"
                    @selected="set_status"
                    ref="status_dropdown"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Personal Information Section -->
          <div class="box mb-5">
            <h6 class="title is-6 mb-4">{{ $t("personal information") }}</h6>
            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("first name") }} *</label>
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      v-model="meta.first_name"
                      :placeholder="$t('enter first name')"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("second name") }}</label>
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      v-model="meta.second_name"
                      :placeholder="$t('enter second name')"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("first surname") }} *</label>
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      v-model="meta.first_surname"
                      :placeholder="$t('enter first surname')"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("second surname") }}</label>
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      v-model="meta.second_surname"
                      :placeholder="$t('enter second surname')"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("birth date") }}</label>
                  <div class="control">
                    <input
                      type="date"
                      class="input"
                      v-model="meta.birth_date"
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("admission date") }}</label>
                  <div class="control">
                    <input
                      type="date"
                      class="input"
                      v-model="meta.admission_date"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Work Information Section -->
          <div class="box mb-5">
            <h6 class="title is-6 mb-4">{{ $t("work information") }}</h6>
            <div class="columns">
              <div class="column">
                <div class="field">
                  <generic-dropdown
                    :title="$t('immediate superior')"
                    :placeholder="$t('select one')"
                    :is_multiple="false"
                    url="/staff/member/catalog"
                    :display_function="member_display"
                    value_field="member_id"
                    @selected="set_immediate_superior"
                    ref="immediate_superior_dropdown"
                  />
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("salary") }}</label>
                  <div class="control">
                    <input
                      type="number"
                      class="input"
                      v-model="meta.salary"
                      :placeholder="$t('enter salary amount')"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Banking Information Section -->
          <div class="box" v-if="meta.settings?.banking_info">
            <h6 class="title is-6 mb-4">{{ $t("banking information") }}</h6>
            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("bank name") }}</label>
                  <div class="control">
                    <input
                      v-model="meta.settings.banking_info.bank_name"
                      class="input"
                      type="text"
                      :placeholder="$t('enter bank name')"
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("account type") }}</label>
                  <div class="control">
                    <div class="select is-fullwidth">
                      <select v-model="meta.settings.banking_info.account_type">
                        <option value="">
                          {{ $t("select account type") }}
                        </option>
                        <option value="savings">{{ $t("savings") }}</option>
                        <option value="checking">{{ $t("checking") }}</option>
                        <option value="business">{{ $t("business") }}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("account holder name") }}</label>
                  <div class="control">
                    <input
                      v-model="meta.settings.banking_info.account_holder_name"
                      class="input"
                      type="text"
                      :placeholder="$t('enter account holder name')"
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("account number") }}</label>
                  <div class="control">
                    <input
                      v-model="meta.settings.banking_info.account_number"
                      class="input"
                      type="text"
                      :placeholder="$t('enter account number')"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Images/Photos Tab -->
      <div class="tab-content is-hidden" data-tab="images">
        <!-- Upload Section -->
        <div class="box mb-5">
          <h6 class="title is-6 mb-4">{{ $t("upload photos") }}</h6>
          <div class="field">
            <div class="control">
              <generic-uploader
                ref="generic_uploader"
                :idle_label="$t('drop photos here')"
                endpoint_url="/staff/member/photo"
                :allow_multiple="true"
                @error="error_uploading"
                @success="success_uploading"
              />
            </div>
          </div>
        </div>

        <!-- Photo Gallery -->
        <div class="box" v-if="meta?.settings?.images?.length">
          <h6 class="title is-6 mb-4">{{ $t("photo gallery") }}</h6>
          <div class="grid is-col-min-7">
            <div
              class="cell"
              v-for="(photo, midx) in meta.settings.images"
              :key="midx"
            >
              <div class="card">
                <div class="card-image">
                  <figure class="image is-128x128 m-auto py-3">
                    <v-lazy-image
                      :src="`${api_host}/drive/static/${photo.final_name}`"
                      :alt="`${$t('photo')} ${midx + 1}`"
                    />
                  </figure>
                </div>
                <footer class="card-footer">
                  <a
                    class="card-footer-item has-text-danger"
                    @click="meta.settings.images.splice(midx, 1)"
                    :title="$t('delete')"
                  >
                    <span class="icon">
                      <i class="fa fa-trash"></i>
                    </span>
                    <span>{{ $t("delete") }}</span>
                  </a>
                </footer>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div class="box has-text-centered" v-else>
          <div class="content">
            <p class="has-text-grey">
              <span class="icon is-large">
                <i class="fa fa-camera fa-2x"></i>
              </span>
            </p>
            <p class="has-text-grey">{{ $t("no photos uploaded yet") }}</p>
          </div>
        </div>
      </div>

      <!-- Custom Fields Tab -->
      <div class="tab-content is-hidden" data-tab="custom_fields">
        <div class="box">
          <!-- <h6 class="title is-6 mb-4">{{ $t("custom fields") }}</h6> -->
          <custom-fields
            :module_settings="module_settings"
            ref="custom_fields"
          />
        </div>
      </div>
    </div>

    <!-- Error Notification -->
    <div class="notification is-danger mt-5" v-if="error">
      <button class="delete" @click="error = ''"></button>
      <p v-html="error"></p>
    </div>
  </generic-modal>
</template>
<script>
import {
  put,
  error_message,
  get,
  post,
  error_alert,
  api_host,
  generate_error_message_text,
  get_currency_settings,
  change_active_tab,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericUploader from "@/components/generic_uploader/Index.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";
import VLazyImage from "v-lazy-image";
import CustomFields from "./CustomFields.vue";

export default {
  name: "MemberModal",
  components: {
    GenericModal,
    GenericUploader,
    VLazyImage,
    GenericDropdown,
    CustomFields,
  },
  props: {
    module_settings: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      api_host,
      buttons: [
        {
          text: this.$t("save"),
          class: "is-primary is-rounded",
          icon: "fa fa-save",
          click: this.save,
          disabled: "this.is_form_invalid",
        },
        {
          text: this.$t("cancel"),
          class: "is-rounded",
          icon: "fa fa-close",
          click: this.close_modal,
        },
      ],
      meta: {},
      error: "",
      // uuid: "",
    };
  },
  computed: {
    is_form_invalid() {
      let v = this;
      // return false;
      return !v.meta.first_name || !v.meta.first_surname;
    },
  },
  methods: {
    change_active_tab,
    get_currency_settings,
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    show_modal() {
      let v = this;
      v.set_initial_state();
      v.$refs.generic_modal.show_modal();
    },
    edit_modal(member_uuid) {
      let v = this;
      v.set_initial_state();
      v.member_uuid = member_uuid;
      get("/staff/member", { uuid: member_uuid })
        .then((response) => {
          if (response && response.settings && !response.settings.banking_info)
            response.settings.banking_info = {};
          v.meta = response;
          if (response.settings && response.settings.custom_fields)
            v.$refs.custom_fields.set_data(response.settings.custom_fields);

          if (response.modul_user_id)
            v.$refs.modul_user_dropdown.set_value(response.modul_user_id);

          if (response.immediate_superior_member_id)
            v.$refs.immediate_superior_dropdown.set_value(
              response.immediate_superior_member_id
            );

          if (response.status_id)
            v.$refs.status_dropdown.set_value(response.status_id);
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        })
        .finally(() => {
          v.$refs.generic_modal.show_modal();
        });
    },
    save() {
      let v = this;
      v.meta.settings.custom_fields = v.$refs.custom_fields.get_data();
      let data = { ...v.meta };
      if (!v.member_uuid) {
        put("/staff/member", data)
          .then(() => {
            v.$emit("success");
            v.close_modal();
          })
          .catch((error) => {
            v.error = generate_error_message_text(error);
          });
      } else {
        post("/staff/member", data)
          .then(() => {
            v.$emit("success");
            v.close_modal();
          })
          .catch((error) => {
            v.error = generate_error_message_text(error);
          });
      }
    },
    set_initial_state() {
      let v = this;
      v.meta = {
        settings: {
          banking_info: {},
        },
      };
      v.error = "";
      v.member_uuid = "";
      v.$refs.generic_uploader.clear();
      v.$refs.modul_user_dropdown.clear();
      v.$refs.immediate_superior_dropdown.clear();
      v.$refs.status_dropdown.clear();
      v.$refs.custom_fields.set_initial_state();
      // v.uuid = "";
    },
    error_uploading(error) {
      let v = this;
      v.error = generate_error_message_text(error);
    },
    success_uploading(response) {
      let v = this;
      // v.$refs.generic_uploader.clear();
      let rspnse = JSON.parse(response.serverId);
      let data = {
        file_id: rspnse.file.file_id,
        final_name: rspnse.final_name,
        meta: rspnse.file.settings,
        original_name: rspnse.file.name,
      };
      if (v.meta.settings.images) {
        v.meta.settings.images.push(data);
      } else {
        v.meta.settings.images = [data];
      }
    },
    set_modul_user(user) {
      let v = this;
      if (user) v.meta.modul_user_id = user.user_id;
    },
    set_immediate_superior(member) {
      let v = this;
      if (member) v.meta.immediate_superior_member_id = member.member_id;
    },
    member_display(member) {
      return `${member.first_name} ${member.first_surname}`;
    },
    set_status(status) {
      let v = this;
      if (status) v.meta.status_id = status.status_id;
    },
  },
  mounted() {},
  created() {},
  watch: {
    is_form_invalid() {
      let v = this;
      v.buttons[0].disabled = v.is_form_invalid;
    },
  },
};
</script>
<style></style>
